import React, { useState, useEffect } from "react";
import { Switch, Row, Loading } from "@nextui-org/react";
import { useSelector } from "react-redux";
import { fetchWithJson } from "../../lib/api";
import { mstore } from "../../store";
import endpoint from "../../config/endpoint.json";
import { error, success } from "../../lib/userAlerts";

const NewsFeed = () => {
  const [state, setState] = useState({
    newsFeedSettings: {},
    togglingId: null
  });

  const Access = useSelector(state => state.navigation.Access);
  const clientId = useSelector(state => 
    state.client?.id || mstore.getPath(["user", "company_id"])
  );

  const fetchNewsFeedSettingsDetails = async () => {
    try {
      const response = await fetchWithJson(endpoint.fetchNewsFeesSettings, {});
      const data = await response.json();
      
      if (data.success) {
        setState(prev => ({ ...prev, newsFeedSettings: data.newsFeedSettings }));
      } else {
        error("Oops! error occurred during fetching news feed settings details!");
      }
    } catch (err) {
      error("Oops! error occurred during fetching news feed settings details!");
      console.error(err.message);
    }
  };

  const handleIndustryNewsSwitch = async (disable) => {
    setState(prev => ({ ...prev, togglingId: 'industry' }));
    try {
      const response = await fetchWithJson(endpoint.updateHasSubscribedIndustryNews, {
        industyNewsAccess: disable
          ? 0
          : state.newsFeedSettings.has_subscribed_industry_news === 1
          ? 0
          : 1,
      });
      const data = await response.json();

      if (state.newsFeedSettings.has_subscribed_industry_news === 1) {
        success("You have successfully deactivated the Industry Content in Feed feature");
      } else if (!disable) {
        success("You have successfully activated the Industry Content in Feed feature");
      }

      await fetchNewsFeedSettingsDetails();
    } catch (err) {
      error("Oops! error occurred");
      console.error(err.message);
    } finally {
      setState(prev => ({ ...prev, togglingId: null }));
    }
  };

  const handleAppInfoSwitch = async (disable) => {
    setState(prev => ({ ...prev, togglingId: 'app' }));
    try {
      const response = await fetchWithJson(endpoint.updateHasSubscribedAppInfo, {
        appInfoAccess: disable
          ? 0
          : state.newsFeedSettings.has_subscribed_app_info === 1
          ? 0
          : 1,
      });
      const data = await response.json();

      if (state.newsFeedSettings.has_subscribed_app_info === 1) {
        success("You have successfully deactivated the App Info and News in Feed feature");
      } else if (!disable) {
        success("You have successfully activated the App Info and News in Feed feature");
      }

      await fetchNewsFeedSettingsDetails();
    } catch (err) {
      error("Oops! error occurred");
      console.error(err.message);
    } finally {
      setState(prev => ({ ...prev, togglingId: null }));
    }
  };

  useEffect(() => {
    fetchNewsFeedSettingsDetails();
  }, [clientId]);

  if (Access.allow_blog_posts !== 1) {
    return (
      <div role="alert" className="max-w-2xl mx-auto mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg shadow-sm">
        <div className="flex items-center space-x-3">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-yellow-800">Please subscribe to the <b>News Feed</b> feature</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-dotted">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800">
              Show MTT Industry Content in Feed
            </h2>
          </div>

          <Row justify="space-between" className="items-center">
            <div className="text-gray-700 font-medium">
              Enable / Disable feature
            </div>
            {state.togglingId === 'industry' ? (
              <div className="flex items-center justify-center">
                <Loading size="sm" color="primary" />
              </div>
            ) : (
              <Switch
                size="sm"
                checked={state.newsFeedSettings.has_subscribed_industry_news === 1}
                onChange={() => handleIndustryNewsSwitch(false)}
                className="hover:opacity-80 transition-opacity"
              />
            )}
          </Row>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-dotted">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800">
              Show MTT App Info and News in Feed
            </h2>
          </div>

          <Row justify="space-between" className="items-center">
            <div className="text-gray-700 font-medium">
              Enable / Disable feature
            </div>
            {state.togglingId === 'app' ? (
              <div className="flex items-center justify-center">
                <Loading size="sm" color="primary" />
              </div>
            ) : (
              <Switch
                size="sm"
                checked={state.newsFeedSettings.has_subscribed_app_info === 1}
                onChange={() => handleAppInfoSwitch(false)}
                className="hover:opacity-80 transition-opacity"
              />
            )}
          </Row>
        </div>
      </div>
    </div>
  );
};

export default NewsFeed;